import type { MenuProps } from 'antd';
import { useChatContext } from '@/contexts/chat/context';
import { useRefs } from '@/scripts/hooks';
import { useSessionContext } from '@/contexts/session/context';
import { MediaType } from '@/contexts/chat/scripts/hooks/useQuickVO/types';
import type { User } from '@/configs/apis/user';
import { Loading } from '@/components/loading';
import useDragMove from '../../../hooks/useDragMove';
import CallTimer from '../callOperate/components/callTimer';
import styles from './index.module.scss';
import FixedIcon from './images/fixed.svg?react';
import MoreIcon from './images/more.svg?react';
import UnMuteIcon from './images/un_mute.svg?react';
import CallIcon from './images/call-icon.svg?react';
import ScreenIcon from './images/screen.svg?react';
import CardMoreOperate from './components/cardMoreOperate';
import DrawSpeak from './components/drawSpeak';
import { CardMoreOperateKey } from './components/cardMoreOperate/type';
import type { CallMemberItem } from './type';
import { SubscribeStatus } from './type';

const CallCard = ({
  member,
  user,
  mediaModalZoomIn,
  isDragging,
  isOnlyOne = false,
  rowsHeight = 300,
  index = -1,
  hasFixed = false,
  onFixed,
  onSubscribe,
}: {
  member: CallMemberItem;
  user: User;
  mediaModalZoomIn: boolean;
  isDragging?: boolean;
  isOnlyOne?: boolean;
  rowsHeight?: number;
  index?: number;
  hasFixed?: boolean;
  onFixed: () => void;
  onSubscribe: (status: SubscribeStatus) => void;
}) => {
  const {
    chatMediaCall,
    chatMediaQuickVO,
    mediaIsPutThrough,
    mediaIsReady,
    mediaStartTime,
  } = useChatContext();
  const { session } = useSessionContext();
  const {
    roomUsersStreams,
    updateUserStreams,
    setVolume,
    subscribe,
    subscribeCollection,
    stopSubscribe,
  } = chatMediaQuickVO;

  const isSelf = user?.userId === member.id;
  const isSelfCard = user?.userId === member.userId;
  const isSelfUnFixed = isSelf && !member.fixed;

  const { dragRef, style } = useDragMove({
    disabled: !(isSelfUnFixed && chatMediaCall.mediaModalZoomIn) || isOnlyOne,
    marginBottom: 76,
    thresholdBottom: 76,
    showLabelLeft: false,
    showLabelRight: false,
  });

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const refs = useRefs({
    updateUserStreams,
    subscribe,
    subscribeCollection,
    stopSubscribe,
    onSubscribe,
  });

  const ringing = useMemo(() => !mediaIsPutThrough, [mediaIsPutThrough]);

  const callUserInfo = useMemo(() => member.roomUser, [member.roomUser]);

  const callUserStream = useMemo(
    () => roomUsersStreams?.find((roomUser) => roomUser.userId === member.userId),
    [member.userId, roomUsersStreams]
  );

  const track = useMemo(
    () =>
      callUserInfo?.tracks?.find((track) =>
        member?.screenSharing
          ? track.mediaType === MediaType.SCREENSHARING_VIDEO
          : track.mediaType === MediaType.MICROPHONECAMERA_VIDEO
      ),
    [callUserInfo?.tracks, member?.screenSharing]
  );

  const handleClickMore: MenuProps['onClick'] = useCallback(
    ({ key }: { key: string }) => {
      switch (key) {
        case CardMoreOperateKey.MUTE:
          setVolume(callUserInfo!.id, MediaType.MICROPHONECAMERA_AUDIO, 0);
          break;
        case CardMoreOperateKey.WATCH:
          break;
        case CardMoreOperateKey.PIN:
          onFixed();
          break;
        case CardMoreOperateKey.FULL_SCREEN:
          dragRef.current?.requestFullscreen();
          break;
        default:
          break;
      }
    },
    [callUserInfo, dragRef, onFixed, setVolume]
  );

  const handleRetrySubscribe = useCallback(async () => {
    if (track?.trackName) {
      const { subscribeCollection, onSubscribe } = refs.current;
      onSubscribe(SubscribeStatus.SUBSCRIBEING);
      const subscribeInfo = await subscribeCollection(
        [track?.trackName],
        'subscribe'
      )?.catch(() => {
        onSubscribe(SubscribeStatus.SUBSCRIBE_FAIL);
      });
      if (subscribeInfo?.detail?.fail?.includes(track?.trackName)) {
        onSubscribe(SubscribeStatus.SUBSCRIBE_FAIL);
      }
    }
  }, [refs, track?.trackName]);

  useEffect(() => {
    const { subscribeCollection, onSubscribe } = refs.current;
    if (
      isSelfCard ||
      (!callUserInfo?.callActionMap?.microphoneCamera_video &&
        !member?.screenSharing) ||
      isDragging
    ) {
      if (
        !callUserInfo?.callActionMap?.microphoneCamera_video &&
        !member?.screenSharing &&
        member.subscribeStatus === SubscribeStatus.SUBSCRIBEING
      ) {
        onSubscribe(SubscribeStatus.DEFAULT);
      }
      return;
    }

    const observer_ = new IntersectionObserver(
      async ([entry]) => {
        const trackName = track?.trackName;
        if (trackName) {
          if (entry.isIntersecting) {
            if (
              member.subscribeStatus === SubscribeStatus.SUBSCRIBE_CANCEL ||
              member.subscribeStatus === SubscribeStatus.DEFAULT
            ) {
              handleRetrySubscribe();
            }
          } else {
            if (
              (!mediaModalZoomIn && index < 6) ||
              member.fixed ||
              document.fullscreenElement === dragRef.current
            ) {
              return;
            }

            if (member.subscribeStatus === SubscribeStatus.SUBSCRIBE_SUCCESS) {
              subscribeCollection([trackName], 'stopSubscribe')
                ?.catch(() => {})
                .finally(() => {
                  onSubscribe(SubscribeStatus.SUBSCRIBE_CANCEL);
                });
            }
          }
        }
      },
      {
        threshold: 0.1,
        root: document.querySelector('.mediaModalRight'),
        rootMargin: `${(hasFixed && index < 6 ? 0 : 2) * rowsHeight}px 0px ${(hasFixed && index < 6 ? 0 : 2) * rowsHeight}px 0px`,
      }
    );
    if (dragRef.current) {
      observer_.observe(dragRef.current);
    }
    return () => {
      observer_.disconnect();
    };
  }, [
    dragRef,
    refs,
    callUserInfo?.callActionMap?.microphoneCamera_video,
    track,
    isSelfCard,
    member.subscribeStatus,
    member?.screenSharing,
    mediaModalZoomIn,
    isDragging,
    index,
    rowsHeight,
    member.fixed,
    hasFixed,
    handleRetrySubscribe,
  ]);

  useEffect(() => {
    const { updateUserStreams, onSubscribe } = refs.current;
    if (callUserInfo?.id && member?.screenSharing) {
      updateUserStreams({
        user: { id: callUserInfo?.id },
        mediaDoms: { screenVideoRef: videoRef },
        onplay: () => {
          if (
            track?.msid &&
            SubscribeStatus.SUBSCRIBE_SUCCESS !== member.subscribeStatus
          ) {
            onSubscribe(SubscribeStatus.SUBSCRIBE_SUCCESS);
          }
        },
      });
    }
  }, [
    refs,
    track?.msid,
    member?.screenSharing,
    member.subscribeStatus,
    callUserInfo?.id,
    callUserInfo?.callActionMap?.screenSharing_video,
    callUserInfo?.updateStreams?.screenSharing_video,
    callUserStream?.updateStreams?.screenSharing_video,
  ]);

  useEffect(() => {
    const { updateUserStreams, onSubscribe } = refs.current;
    if (callUserInfo?.id && !member?.screenSharing) {
      updateUserStreams({
        user: { id: callUserInfo?.id },
        mediaDoms: { videoRef },
        onplay: () => {
          if (
            track?.msid &&
            SubscribeStatus.SUBSCRIBE_SUCCESS !== member.subscribeStatus
          ) {
            onSubscribe(SubscribeStatus.SUBSCRIBE_SUCCESS);
          }
        },
      });
    }
  }, [
    refs,
    track?.msid,
    member?.screenSharing,
    member.subscribeStatus,
    callUserInfo?.id,
    callUserInfo?.callActionMap?.microphoneCamera_video,
    callUserInfo?.updateStreams?.microphoneCamera_video,
    callUserStream?.updateStreams?.microphoneCamera_video,
  ]);

  return (
    <div
      style={style}
      ref={dragRef}
      className={cls(
        styles.callCard,
        isSelfUnFixed && !isOnlyOne && styles.floatCallCard,
        isSelfUnFixed && !isOnlyOne && ringing && styles.hidden,
        mediaModalZoomIn
          ? styles.zoomIn
          : isSelfUnFixed && !isOnlyOne
            ? styles.selfZoomOut
            : styles.zoomOut,
        !ringing && styles.zoomIn
      )}
    >
      <video
        className={cls(
          styles.video,
          (ringing ||
            (!member?.screenSharing &&
              !callUserInfo?.callActionMap?.microphoneCamera_video) ||
            member.subscribeStatus === SubscribeStatus.SUBSCRIBE_FAIL ||
            member.subscribeStatus === SubscribeStatus.SUBSCRIBE_CANCEL ||
            member.subscribeStatus === SubscribeStatus.SUBSCRIBEING) &&
            styles.closeVideo
        )}
        playsInline
        autoPlay
        ref={videoRef}
      ></video>
      {member.subscribeStatus === SubscribeStatus.SUBSCRIBEING && (
        <div className={styles.subscribeIng}>
          <Loading text={null}></Loading>
        </div>
      )}
      {/* {member.subscribeStatus === SubscribeStatus.SUBSCRIBE_FAIL && (
        <div className={styles.subscribeIng} onClick={handleRetrySubscribe}>
          Subscription failed（retry）
        </div>
      )} */}
      {(ringing ||
        (!member?.screenSharing &&
          !callUserInfo?.callActionMap?.microphoneCamera_video) ||
        member.subscribeStatus === SubscribeStatus.SUBSCRIBE_FAIL ||
        member.subscribeStatus === SubscribeStatus.SUBSCRIBE_CANCEL) && (
        <div className={styles.avatarBox}>
          <MyAvatar
            className={cls(
              mediaModalZoomIn
                ? isSelfUnFixed && !isOnlyOne
                  ? styles.avatar
                  : styles.avatarZoomIn
                : isSelfUnFixed && !isOnlyOne
                  ? styles.avatarZoomOut
                  : styles.avatar
            )}
            src={member.avatar!}
            info={member}
          />
          {mediaIsPutThrough && !mediaIsReady && (
            <div
              className={cls(
                styles.tipsBar,
                styles.connecting,
                !isSelfUnFixed && !mediaModalZoomIn && styles.show
              )}
            >
              <CallIcon />
              <div className={styles.textBar}>Connecting...</div>
            </div>
          )}
          {mediaIsPutThrough && mediaIsReady && (
            <div
              className={cls(
                styles.tipsBar,
                styles.mediaIsPutThrough,
                !isSelfUnFixed && !mediaModalZoomIn && styles.show
              )}
            >
              {callUserInfo?.callActionMap?.microphoneCamera_audio &&
              !mediaModalZoomIn ? (
                <DrawSpeak
                  callUserInfo={callUserInfo}
                  callUserStream={callUserStream}
                  member={member}
                ></DrawSpeak>
              ) : (
                <UnMuteIcon />
              )}
              <CallTimer startTime={mediaStartTime} />
            </div>
          )}
          {ringing && (
            <div className={styles.statusBar}>
              <div className={styles.name}>{member.name}</div>
              {ringing &&
                chatMediaCall.chatMediaInfo?.caller?.userId ===
                  session.user?.userId && (
                  <div className={styles.tipsBar}>
                    <CallIcon />
                    <div className={styles.textBar}>Ringing...</div>
                  </div>
                )}
            </div>
          )}
        </div>
      )}

      {mediaModalZoomIn && (
        <>
          {member.fixed && (
            <div className={styles.fixed}>
              <FixedIcon />
            </div>
          )}

          {mediaIsPutThrough && (
            <div className={styles.operate}>
              <div className={styles.info}>
                {member.screenSharing ? (
                  <ScreenIcon />
                ) : callUserInfo?.callActionMap?.microphoneCamera_audio ? (
                  <DrawSpeak
                    callUserInfo={callUserInfo}
                    callUserStream={callUserStream}
                    member={member}
                  ></DrawSpeak>
                ) : (
                  <UnMuteIcon />
                )}
                <div>
                  {member.userId === user.userId ? 'Me' : member.name}
                  {/* {member?.status === CallMemberStatus.TALKING && '(Calling)'} */}
                </div>
              </div>
              {!isSelfUnFixed && (
                <CardMoreOperate className={styles.more} onClick={handleClickMore}>
                  <MoreIcon />
                </CardMoreOperate>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CallCard;
